import type { DirectoryDto } from '@dbrain/shared';
import type { ChatItem } from './conversation-state.js';

/**
 * File names in an answer that are files of the workspace become chips that
 * open the file in the panel (D-99). Known files: what the agent wrote,
 * edited or read in this conversation, and the workspace's top folder while
 * its environment runs (reading the list never starts it).
 */
const FILE_TOOLS = new Set(['write_file', 'append_file', 'edit', 'read_file']);

/** "./a.py", "/workspace/a.py" and "a.py" name the same file. */
export function workspacePath(raw: string): string | null {
  const path = raw
    .trim()
    .replace(/^\/workspace\//, '')
    .replace(/^(\.\/)+/, '');
  if (!path || path.startsWith('/') || path.split('/').includes('..') || path.endsWith('/')) return null;
  return path;
}

export function knownFiles(items: ChatItem[], listing?: Pick<DirectoryDto, 'entries'> | null): string[] {
  const found = new Set<string>();
  for (const item of items) {
    if (item.kind !== 'tool' || item.status !== 'done' || !FILE_TOOLS.has(item.tool)) continue;
    try {
      const args = JSON.parse(item.arguments) as { path?: unknown };
      const path = typeof args.path === 'string' ? workspacePath(args.path) : null;
      if (path) found.add(path);
    } catch {
      // arguments that are not JSON name no file
    }
  }
  for (const entry of listing?.entries ?? []) if (entry.type === 'file') found.add(entry.name);
  return [...found];
}

/** The workspace file a name in an answer means: the same path, or the only file with that name. */
export function fileResolver(paths: string[]): (name: string) => string | null {
  const exact = new Set(paths);
  const byName = new Map<string, string[]>();
  for (const path of paths) {
    const name = path.slice(path.lastIndexOf('/') + 1);
    byName.set(name, [...(byName.get(name) ?? []), path]);
  }
  return (raw) => {
    const path = workspacePath(raw);
    if (!path) return null;
    if (exact.has(path)) return path;
    const same = byName.get(path);
    return same && same.length === 1 ? (same[0] ?? null) : null;
  };
}

/** a file name with an extension, alone or with folders ("web/index.html"), not part of a URL or a longer word */
const FILE_NAME = /(?<![\p{L}\p{N}_./:-])((?:[\p{L}\p{N}_-]+\/)*[\p{L}\p{N}_-][\p{L}\p{N}_.-]*\.[\p{L}\p{N}]{1,8})(?![\p{L}\p{N}_/-])/gu;

/** Where a text names files of the workspace: the place, the words as written and the file they mean. */
export function fileMentions(text: string, resolve: (name: string) => string | null): { at: number; name: string; path: string }[] {
  if (!text.includes('.')) return [];
  const found: { at: number; name: string; path: string }[] = [];
  for (const match of text.matchAll(FILE_NAME)) {
    const name = match[1] as string;
    const path = resolve(name);
    if (path) found.push({ at: match.index ?? 0, name, path });
  }
  return found;
}
